import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { newsletter } from '@/lib/api';

const NewsletterUnsubscribePage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [state, setState] = useState('loading'); // loading | success | already | error

  useEffect(() => {
    if (!token || token.length < 16) {
      setState('error');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await newsletter.unsubscribe(token);
        if (cancelled) return;
        if (res.data?.already_unsubscribed) {
          setState('already');
        } else {
          setState('success');
        }
      } catch {
        if (!cancelled) setState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src="/logo.png" alt="Turkish E-Market" className="h-10 w-auto" />
          </Link>
        </div>

        <Card>
          <CardContent className="pt-8 pb-8 text-center">
            {state === 'loading' && (
              <>
                <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">{t('newsletterUnsubscribe.processing')}</p>
              </>
            )}
            {state === 'success' && (
              <>
                <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <h1 className="text-xl font-semibold mb-2">{t('newsletterUnsubscribe.successTitle')}</h1>
                <p className="text-muted-foreground text-sm">{t('newsletterUnsubscribe.successDesc')}</p>
              </>
            )}
            {state === 'already' && (
              <>
                <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h1 className="text-xl font-semibold mb-2">{t('newsletterUnsubscribe.alreadyTitle')}</h1>
                <p className="text-muted-foreground text-sm">{t('newsletterUnsubscribe.alreadyDesc')}</p>
              </>
            )}
            {state === 'error' && (
              <>
                <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
                <h1 className="text-xl font-semibold mb-2">{t('newsletterUnsubscribe.errorTitle')}</h1>
                <p className="text-muted-foreground text-sm">{t('newsletterUnsubscribe.errorDesc')}</p>
              </>
            )}
            <Button asChild className="mt-6" variant="outline">
              <Link to="/">{t('newsletterUnsubscribe.backHome')}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NewsletterUnsubscribePage;
